/**
 * brioCAST Advertiser Campaigns Page
 * Drop-in for existing CRA portal
 *
 * Add to renderPage():
 *   case "ad-campaigns": return hasCap(account, "AD_BUYER") ? <AdCampaignsPage account={account} /> : <NoCap cap="AD_BUYER" />;
 * Add to Sidebar:
 *   { id: "ad-campaigns", label: "廣告活動", icon: "📢", cap: "AD_BUYER" }
 *
 * Views: List → Detail → Wizard (internal routing via state)
 */
import { useState, useEffect } from "react";

const API_V1 = "https://api.briolabs.io/api/v1";
async function apiV1(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers };
  const res = await fetch(`${API_V1}${path}`, { ...options, headers, body: options.body ? JSON.stringify(options.body) : undefined });
  const data = await res.json();
  if (!res.ok && !data.success) throw new Error(data.error || "Request failed");
  return data;
}

const t = {
  bg: "#0F1117", card: "#1A1D27", cardHover: "#22262F", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", success: "#00B894", danger: "#E17055",
  warning: "#FDCB6E", text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
};

const STATUS_MAP = {
  draft: { label: "草稿", color: t.textDim, bg: `${t.textDim}18` },
  pending_review: { label: "審核中", color: t.warning, bg: `${t.warning}18` },
  active: { label: "投放中", color: t.success, bg: `${t.success}18` },
  paused: { label: "已暫停", color: t.warning, bg: `${t.warning}18` },
  rejected: { label: "已退回", color: t.danger, bg: `${t.danger}18` },
  completed: { label: "已完成", color: t.textMuted, bg: `${t.textMuted}18` },
  archived: { label: "已封存", color: t.textDim, bg: `${t.textDim}18` },
};

const VENUE_OPTIONS = [
  { value: "clinic", label: "診所", cpm: 80 },
  { value: "restaurant", label: "餐飲", cpm: 60 },
  { value: "temple", label: "寺廟", cpm: 30 },
  { value: "parking", label: "停車場", cpm: 40 },
  { value: "ev_charging", label: "EV充電站", cpm: 45 },
  { value: "fitness", label: "健身房", cpm: 55 },
  { value: "retail", label: "零售", cpm: 50 },
  { value: "postpartum", label: "月子中心", cpm: 100 },
];

const CITY_OPTIONS = ["taipei", "new_taipei", "taoyuan", "taichung", "tainan", "kaohsiung"];
const CITY_LABELS = { taipei: "台北市", new_taipei: "新北市", taoyuan: "桃園市", taichung: "台中市", tainan: "台南市", kaohsiung: "高雄市" };
const DAYPART_OPTIONS = [
  { value: "morning", label: "早上 6-12" },
  { value: "afternoon", label: "下午 12-18" },
  { value: "evening", label: "晚上 18-22" },
  { value: "night", label: "深夜 22-6" },
];
const DOW_LABELS = ["日", "一", "二", "三", "四", "五", "六"];

const cardStyle = { background: t.card, borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", padding: 24, marginBottom: 16 };
const inputStyle = { width: "100%", padding: "12px 16px", background: "#141720", border: `1px solid ${t.border}`, borderRadius: 10, color: t.text, fontSize: 14, outline: "none", boxSizing: "border-box" };
const btnPrimary = { padding: "12px 24px", borderRadius: 10, border: "none", background: t.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const btnOutline = { padding: "10px 20px", borderRadius: 10, border: `1px solid ${t.border}`, background: "transparent", color: t.text, fontSize: 14, cursor: "pointer" };
const labelStyle = { fontSize: 13, color: t.textMuted, marginBottom: 6, display: "block", fontWeight: 500 };

// ═══════════════════════════════════════════════════
// CAMPAIGN LIST VIEW
// ═══════════════════════════════════════════════════

function CampaignList({ onSelect, onCreate }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    Promise.all([
      apiV1("/advertiser/campaigns"),
      apiV1("/advertiser/dashboard"),
    ]).then(([campRes, dashRes]) => {
      setCampaigns(campRes.data?.campaigns || []);
      setDashboard(dashRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: t.textMuted }}>載入中...</div>;

  return (
    <div>
      {/* Dashboard stats */}
      {dashboard && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { label: "廣告活動", value: dashboard.campaigns || 0, icon: "📢" },
            { label: "投放中", value: dashboard.active || 0, icon: "🟢", color: t.success },
            { label: "總預算", value: `NT$${(dashboard.total_budget || 0).toLocaleString()}`, icon: "💰" },
            { label: "已花費", value: `NT$${Math.round(dashboard.total_spent || 0).toLocaleString()}`, icon: "📊", color: t.warning },
            { label: "總曝光", value: (dashboard.total_impressions || 0).toLocaleString(), icon: "👁" },
          ].map((s) => (
            <div key={s.label} style={{ ...cardStyle, flex: "1 1 150px", minWidth: 140, marginBottom: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 8 }}>{s.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: s.color || t.text }}>{s.value}</div>
                </div>
                <span style={{ fontSize: 24, opacity: 0.5 }}>{s.icon}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Campaign list */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>廣告活動列表</h3>
          <button onClick={onCreate} style={btnPrimary}>＋ 新增廣告活動</button>
        </div>

        {campaigns.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: t.textDim }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📢</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>尚無廣告活動</div>
            <div style={{ fontSize: 13, color: t.textMuted }}>點擊「新增廣告活動」開始投放</div>
          </div>
        ) : campaigns.map((c) => {
          const st = STATUS_MAP[c.status] || STATUS_MAP.draft;
          const budgetPct = c.total_budget_ntd > 0 ? Math.round((c.spent_amount_ntd / c.total_budget_ntd) * 100) : 0;
          return (
            <div key={c.id} onClick={() => onSelect(c.id)} style={{
              display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", marginBottom: 4,
              borderRadius: 10, cursor: "pointer", transition: "background 0.15s",
              border: `1px solid rgba(255,255,255,0.04)`,
            }}
              onMouseEnter={(e) => { e.currentTarget.style.background = t.cardHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: t.textMuted }}>{c.name_zh || ""} · {c.company_name || c.advertiser_id}</div>
              </div>
              <div style={{ textAlign: "center", minWidth: 80 }}>
                <div style={{ fontSize: 12, color: t.textDim, marginBottom: 2 }}>預算</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>NT${c.total_budget_ntd?.toLocaleString()}</div>
                <div style={{ width: 60, height: 3, background: t.border, borderRadius: 2, marginTop: 4, position: "relative" }}>
                  <div style={{ width: `${Math.min(budgetPct, 100)}%`, height: "100%", background: budgetPct > 80 ? t.danger : t.accent, borderRadius: 2 }} />
                </div>
              </div>
              <div style={{ textAlign: "center", minWidth: 60 }}>
                <div style={{ fontSize: 12, color: t.textDim, marginBottom: 2 }}>曝光</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{(c.impressions_delivered || 0).toLocaleString()}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
              <span style={{ color: t.textDim, fontSize: 16 }}>›</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// CAMPAIGN DETAIL VIEW
// ═══════════════════════════════════════════════════

function CampaignDetail({ campaignId, onBack }) {
  const [campaign, setCampaign] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");

  useEffect(() => {
    apiV1(`/advertiser/campaigns/${campaignId}`).then((res) => {
      setCampaign(res.data?.campaign);
      setCreatives(res.data?.creatives || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [campaignId]);

  const doAction = async (action) => {
    setActionLoading(action);
    try {
      await apiV1(`/advertiser/campaigns/${campaignId}/${action}`, { method: "POST" });
      // Reload
      const res = await apiV1(`/advertiser/campaigns/${campaignId}`);
      setCampaign(res.data?.campaign);
    } catch (err) { alert(err.message); }
    finally { setActionLoading(""); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: t.textMuted }}>載入中...</div>;
  if (!campaign) return <div style={{ padding: 40, color: t.textDim }}>找不到廣告活動</div>;

  const st = STATUS_MAP[campaign.status] || STATUS_MAP.draft;
  const targeting = typeof campaign.targeting === "string" ? JSON.parse(campaign.targeting || "{}") : (campaign.targeting || {});

  return (
    <div>
      <button onClick={onBack} style={{ ...btnOutline, marginBottom: 16, fontSize: 13, padding: "6px 14px" }}>← 返回列表</button>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>{campaign.name}</h2>
            <div style={{ fontSize: 13, color: t.textMuted }}>{campaign.name_zh} · {campaign.id}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, padding: "5px 12px", borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
            {campaign.status === "draft" && <button onClick={() => doAction("submit")} disabled={actionLoading === "submit"} style={{ ...btnPrimary, fontSize: 13, padding: "8px 16px" }}>{actionLoading === "submit" ? "送審中..." : "🚀 送審"}</button>}
            {campaign.status === "active" && <button onClick={() => doAction("pause")} style={{ ...btnOutline, fontSize: 13, padding: "8px 14px", borderColor: `${t.warning}40`, color: t.warning }}>⏸ 暫停</button>}
            {campaign.status === "paused" && <button onClick={() => doAction("resume")} style={{ ...btnPrimary, fontSize: 13, padding: "8px 14px" }}>▶ 恢復</button>}
          </div>
        </div>

        {campaign.description && <div style={{ fontSize: 14, color: t.textMuted, marginBottom: 16, lineHeight: 1.6 }}>{campaign.description}</div>}

        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 20 }}>
          {[
            { label: "總預算", value: `NT$${campaign.total_budget_ntd?.toLocaleString()}` },
            { label: "日預算", value: `NT$${campaign.daily_budget_ntd?.toLocaleString()}` },
            { label: "已花費", value: `NT$${Math.round(campaign.spent_amount_ntd || 0).toLocaleString()}` },
            { label: "CPM", value: `NT$${campaign.cpm_rate}` },
            { label: "投放期間", value: `${campaign.start_date} ~ ${campaign.end_date}` },
            { label: "曝光次數", value: (campaign.impressions_delivered || 0).toLocaleString() },
          ].map((item) => (
            <div key={item.label}>
              <div style={{ fontSize: 12, color: t.textDim }}>{item.label}</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2 }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Targeting summary */}
        <div style={{ background: "#141720", borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: t.accentLight }}>🎯 投放設定</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 13 }}>
            <div><span style={{ color: t.textDim }}>場景:</span> {(campaign.venue_categories?.length > 0 ? campaign.venue_categories : targeting.scenes || []).map((v) => VENUE_OPTIONS.find((o) => o.value === v)?.label || v).join(", ") || "全場景"}</div>
            <div><span style={{ color: t.textDim }}>城市:</span> {(campaign.target_cities?.length > 0 ? campaign.target_cities : targeting.cities || []).map((c) => CITY_LABELS[c] || c).join(", ") || "全台"}</div>
            <div><span style={{ color: t.textDim }}>頻次上限:</span> {campaign.frequency_cap_daily || 3}/日</div>
          </div>
        </div>
      </div>

      {/* Creatives */}
      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>📎 廣告素材 ({creatives.length})</h3>
        {creatives.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: t.textDim, fontSize: 13 }}>尚無素材 — 請在草稿階段上傳</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {creatives.map((cr) => (
              <div key={cr.id} style={{ background: "#141720", borderRadius: 10, padding: 12 }}>
                {cr.thumbnail_url ? (
                  <div style={{ width: "100%", height: 100, borderRadius: 8, background: `url(${cr.thumbnail_url}) center/cover`, marginBottom: 8 }} />
                ) : (
                  <div style={{ width: "100%", height: 100, borderRadius: 8, background: t.border, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8, fontSize: 28, color: t.textDim }}>
                    {cr.creative_type === "video" ? "🎬" : "🖼"}
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 600 }}>{cr.name}</div>
                <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>
                  {cr.creative_type} · {cr.duration_seconds ? `${cr.duration_seconds}s` : "靜態"}
                  {cr.width && cr.height ? ` · ${cr.width}×${cr.height}` : ""}
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 999, marginTop: 4, display: "inline-block", background: cr.is_approved ? `${t.success}18` : `${t.warning}18`, color: cr.is_approved ? t.success : t.warning }}>
                  {cr.is_approved ? "已審核" : "待審核"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// CAMPAIGN WIZARD (5 Steps)
// ═══════════════════════════════════════════════════

function CampaignWizard({ onComplete, onCancel }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "", name_zh: "", description: "",
    total_budget_ntd: 15000, daily_budget_ntd: 500, cpm_rate: 50,
    start_date: "", end_date: "",
    venue_categories: [], target_cities: [], daypart: [],
    days_of_week: [0, 1, 2, 3, 4, 5, 6],
    frequency_cap_daily: 3,
    campaign_type: "standard",
    creative_name: "", creative_type: "video", creative_url: "",
  });

  const updateForm = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));
  const toggleArray = (key, val) => {
    setForm((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val] };
    });
  };

  const STEPS = [
    { num: 1, label: "基本資訊" },
    { num: 2, label: "投放目標" },
    { num: 3, label: "預算與排期" },
    { num: 4, label: "上傳素材" },
    { num: 5, label: "確認送審" },
  ];

  const canNext = () => {
    if (step === 1) return form.name && form.start_date && form.end_date;
    if (step === 2) return form.venue_categories.length > 0;
    if (step === 3) return form.total_budget_ntd >= 5000;
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true); setError("");
    try {
      const res = await apiV1("/advertiser/campaigns", {
        method: "POST",
        body: {
          name: form.name, name_zh: form.name_zh, description: form.description,
          total_budget_ntd: form.total_budget_ntd, daily_budget_ntd: form.daily_budget_ntd,
          cpm_rate: form.cpm_rate, start_date: form.start_date, end_date: form.end_date,
          venue_categories: form.venue_categories, target_cities: form.target_cities,
          daypart: form.daypart, days_of_week: form.days_of_week,
          frequency_cap_daily: form.frequency_cap_daily, campaign_type: form.campaign_type,
        },
      });

      const campId = res.data?.campaign_id;

      // Upload creative if provided
      if (campId && form.creative_url) {
        await apiV1(`/advertiser/campaigns/${campId}/creatives`, {
          method: "POST",
          body: {
            name: form.creative_name || form.name,
            creative_type: form.creative_type,
            file_url: form.creative_url,
            duration_seconds: form.creative_type === "video" ? 15 : null,
          },
        });
      }

      onComplete(campId);
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  };

  const estImpressions = Math.round((form.total_budget_ntd / (form.cpm_rate || 50)) * 1000);

  return (
    <div>
      <button onClick={onCancel} style={{ ...btnOutline, marginBottom: 16, fontSize: 13, padding: "6px 14px" }}>← 取消</button>

      {/* Step indicator */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
        {STEPS.map((s) => (
          <div key={s.num} style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              width: "100%", height: 3, borderRadius: 2, marginBottom: 6,
              background: step >= s.num ? t.accent : t.border,
              transition: "background 0.3s",
            }} />
            <span style={{ fontSize: 11, color: step >= s.num ? t.accentLight : t.textDim, fontWeight: step === s.num ? 700 : 400 }}>
              {s.num}. {s.label}
            </span>
          </div>
        ))}
      </div>

      {error && <div style={{ padding: "10px 14px", marginBottom: 16, fontSize: 13, color: t.danger, background: `${t.danger}15`, borderRadius: 10 }}>⚠ {error}</div>}

      <div style={cardStyle}>
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 18 }}>📝 基本資訊</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>活動名稱 *</label>
              <input value={form.name} onChange={(e) => updateForm("name", e.target.value)} placeholder="e.g. Spring Festival 2026" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>中文名稱</label>
              <input value={form.name_zh} onChange={(e) => updateForm("name_zh", e.target.value)} placeholder="e.g. 春節檔期廣告" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>活動說明</label>
              <textarea value={form.description} onChange={(e) => updateForm("description", e.target.value)} placeholder="描述廣告活動目標..." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>開始日期 *</label>
                <input type="date" value={form.start_date} onChange={(e) => updateForm("start_date", e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>結束日期 *</label>
                <input type="date" value={form.end_date} onChange={(e) => updateForm("end_date", e.target.value)} style={inputStyle} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Targeting */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 18 }}>🎯 投放目標</h3>
            <label style={labelStyle}>場景類型 * (至少選一)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
              {VENUE_OPTIONS.map((v) => {
                const selected = form.venue_categories.includes(v.value);
                return (
                  <button key={v.value} onClick={() => toggleArray("venue_categories", v.value)} style={{
                    padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: `1px solid ${selected ? `${t.accent}60` : t.border}`,
                    background: selected ? `${t.accent}15` : "transparent",
                    color: selected ? t.accentLight : t.textMuted,
                  }}>
                    {v.label} <span style={{ fontSize: 10, opacity: 0.6 }}>CPM ${v.cpm}</span>
                  </button>
                );
              })}
            </div>

            <label style={labelStyle}>投放城市</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
              {CITY_OPTIONS.map((c) => {
                const selected = form.target_cities.includes(c);
                return (
                  <button key={c} onClick={() => toggleArray("target_cities", c)} style={{
                    padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: `1px solid ${selected ? `${t.accent}60` : t.border}`,
                    background: selected ? `${t.accent}15` : "transparent",
                    color: selected ? t.accentLight : t.textMuted,
                  }}>{CITY_LABELS[c]}</button>
                );
              })}
            </div>

            <label style={labelStyle}>時段偏好</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
              {DAYPART_OPTIONS.map((d) => {
                const selected = form.daypart.includes(d.value);
                return (
                  <button key={d.value} onClick={() => toggleArray("daypart", d.value)} style={{
                    padding: "8px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: `1px solid ${selected ? `${t.accent}60` : t.border}`,
                    background: selected ? `${t.accent}15` : "transparent",
                    color: selected ? t.accentLight : t.textMuted,
                  }}>{d.label}</button>
                );
              })}
            </div>

            <label style={labelStyle}>投放星期</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[0, 1, 2, 3, 4, 5, 6].map((d) => {
                const selected = form.days_of_week.includes(d);
                return (
                  <button key={d} onClick={() => toggleArray("days_of_week", d)} style={{
                    width: 36, height: 36, borderRadius: 8, fontSize: 14, cursor: "pointer",
                    border: `1px solid ${selected ? `${t.accent}60` : t.border}`,
                    background: selected ? `${t.accent}25` : "transparent",
                    color: selected ? t.accentLight : t.textDim, fontWeight: 600,
                  }}>{DOW_LABELS[d]}</button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Budget */}
        {step === 3 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 18 }}>💰 預算與排期</h3>
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>總預算 (NTD) * 最低 5,000</label>
                <input type="number" value={form.total_budget_ntd} onChange={(e) => updateForm("total_budget_ntd", parseInt(e.target.value) || 0)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>日預算 (NTD)</label>
                <input type="number" value={form.daily_budget_ntd} onChange={(e) => updateForm("daily_budget_ntd", parseInt(e.target.value) || 0)} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>CPM 出價 (NTD)</label>
                <input type="number" value={form.cpm_rate} onChange={(e) => updateForm("cpm_rate", parseInt(e.target.value) || 50)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>每日頻次上限</label>
                <input type="number" value={form.frequency_cap_daily} onChange={(e) => updateForm("frequency_cap_daily", parseInt(e.target.value) || 3)} style={inputStyle} />
              </div>
            </div>

            {/* Estimate card */}
            <div style={{ background: `${t.accent}10`, border: `1px solid ${t.accent}25`, borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.accentLight, marginBottom: 12 }}>📊 預估投放效果</div>
              <div style={{ display: "flex", gap: 24 }}>
                <div>
                  <div style={{ fontSize: 12, color: t.textDim }}>預估曝光</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{estImpressions.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: t.textDim }}>預估觸及設備</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{Math.round(estImpressions / 50)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: t.textDim }}>平均每日曝光</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{form.daily_budget_ntd > 0 ? Math.round((form.daily_budget_ntd / (form.cpm_rate || 50)) * 1000).toLocaleString() : "—"}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Creative */}
        {step === 4 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 18 }}>📎 上傳素材</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>素材名稱</label>
              <input value={form.creative_name} onChange={(e) => updateForm("creative_name", e.target.value)} placeholder="e.g. 春節15秒影片" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>素材類型</label>
              <div style={{ display: "flex", gap: 8 }}>
                {["video", "image"].map((ct) => (
                  <button key={ct} onClick={() => updateForm("creative_type", ct)} style={{
                    padding: "8px 20px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                    border: `1px solid ${form.creative_type === ct ? `${t.accent}60` : t.border}`,
                    background: form.creative_type === ct ? `${t.accent}15` : "transparent",
                    color: form.creative_type === ct ? t.accentLight : t.textMuted,
                  }}>{ct === "video" ? "🎬 影片" : "🖼 圖片"}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>素材 URL (CDN or MinIO)</label>
              <input value={form.creative_url} onChange={(e) => updateForm("creative_url", e.target.value)} placeholder="https://s3.briolabs.io/creatives/..." style={inputStyle} />
              <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>支援 MP4 (影片) 或 JPG/PNG (圖片) · 建議解析度 1920×1080</div>
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, padding: "12px 16px", background: "#141720", borderRadius: 8 }}>
              💡 素材可在建立活動後隨時新增或替換。送審前至少需一個素材。
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {step === 5 && (
          <div>
            <h3 style={{ margin: "0 0 20px", fontSize: 18 }}>✅ 確認活動資訊</h3>
            <div style={{ background: "#141720", borderRadius: 10, padding: 20 }}>
              {[
                { label: "活動名稱", value: `${form.name} ${form.name_zh ? `(${form.name_zh})` : ""}` },
                { label: "投放期間", value: `${form.start_date} ~ ${form.end_date}` },
                { label: "場景", value: form.venue_categories.map((v) => VENUE_OPTIONS.find((o) => o.value === v)?.label || v).join(", ") },
                { label: "城市", value: form.target_cities.map((c) => CITY_LABELS[c] || c).join(", ") || "全台" },
                { label: "總預算", value: `NT$${form.total_budget_ntd.toLocaleString()}` },
                { label: "CPM", value: `NT$${form.cpm_rate}` },
                { label: "預估曝光", value: estImpressions.toLocaleString() },
                { label: "素材", value: form.creative_url ? `${form.creative_name || "素材"} (${form.creative_type})` : "建立後上傳" },
              ].map((item) => (
                <div key={item.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 13, color: t.textMuted }}>{item.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{item.value}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, fontSize: 12, color: t.textDim, lineHeight: 1.6 }}>
              建立後狀態為「草稿」。上傳素材後可送審，審核約 24-48 小時。
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <button onClick={() => step > 1 ? setStep(step - 1) : onCancel()} style={btnOutline}>
          {step === 1 ? "取消" : "← 上一步"}
        </button>
        {step < 5 ? (
          <button onClick={() => setStep(step + 1)} disabled={!canNext()} style={{ ...btnPrimary, opacity: canNext() ? 1 : 0.4 }}>
            下一步 →
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={saving} style={{ ...btnPrimary, background: saving ? t.textDim : t.success }}>
            {saving ? "建立中..." : "🚀 建立廣告活動"}
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// MAIN COMPONENT (View Router)
// ═══════════════════════════════════════════════════

export default function AdCampaignsPage({ account }) {
  const [view, setView] = useState("list"); // list | detail | wizard
  const [selectedId, setSelectedId] = useState(null);
  const [toast, setToast] = useState("");

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px" }}>📢 廣告活動管理</h2>
      <p style={{ color: t.textMuted, fontSize: 14, marginBottom: 24 }}>建立、管理與追蹤廣告投放活動</p>

      {toast && (
        <div style={{ padding: "12px 16px", marginBottom: 16, background: `${t.success}15`, border: `1px solid ${t.success}30`, borderRadius: 10, fontSize: 13, color: t.success, display: "flex", justifyContent: "space-between" }}>
          <span>✓ {toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: t.success, cursor: "pointer" }}>✕</button>
        </div>
      )}

      {view === "list" && (
        <CampaignList
          onSelect={(id) => { setSelectedId(id); setView("detail"); }}
          onCreate={() => setView("wizard")}
        />
      )}

      {view === "detail" && selectedId && (
        <CampaignDetail
          campaignId={selectedId}
          onBack={() => { setSelectedId(null); setView("list"); }}
        />
      )}

      {view === "wizard" && (
        <CampaignWizard
          onComplete={(campId) => {
            setToast(`廣告活動已建立 (${campId})`);
            setSelectedId(campId);
            setView("detail");
          }}
          onCancel={() => setView("list")}
        />
      )}
    </div>
  );
}
